import { cn } from "@/src/shared/utils/mergeTwClasses";
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  MobileStepper,
  Typography,
} from "@mui/material";
import { FC, useState } from "react";
import { Button } from "../../Button";
import { HintCardProps } from "../lib/type";
import { isNumber } from "lodash";
import { WebShopIcon } from "../../WebShopIcon";
import { strings } from "@/src/shared/strings";
import { useFieldDetailInfoModal } from "@/src/stores/useFieldDetailInfoModal";
import DOMPurify from "dompurify";
import {useAnalytics} from "@/src/components/pages/StepPage/components/useAnalytics";

export const parseLinks = (text: string) => {
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

  const parsedText = text.replace(linkRegex, (_, linkText, url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style=color:#009DDE;text-decoration:underline;>${linkText}</a>`;
  });

  return parsedText;
};

export const HintCard: FC<HintCardProps> = ({
  cardTitle = strings.hintCard.cardTitle,
  contentTitle,
  contentDescription,
  variant = "default",
  hintCardDetailsButtonText = strings.common.more,
  className,
  hintCardDetailsButton,
  maxSteps,
  onNextStep,
  onPrevStep,
  activeStep,
  hintDetailsActions,
  hintDetailsDescription,
  hintDetailsTitle,
  ...rest
}) => {
  const { trackAnalyticEvent } = useAnalytics();
  const openHintDetailsModal = useFieldDetailInfoModal((state) => state.open);

  const isStepeprVisible =
    isNumber(maxSteps) && isNumber(activeStep) && maxSteps > 1;

  const sanitizedDescription = parseLinks(
    DOMPurify.sanitize(contentDescription),
  );

  return (
    <>
      <Card
        className={cn(
          "sticky top-[68px] box-border flex w-[292px] max-w-72 select-none flex-col rounded-md border border-solid border-transparent p-0 transition-all max-md:hidden",
          {
            "border-blue box-shadow-hint-card-focused": variant === "focused",
          },
          className,
        )}
        {...rest}
      >
        <CardHeader
          title={cardTitle}
          className={"bg-super-light-blue text-blue"}
          titleTypographyProps={{
            component: "h4",
            className: "font-semibold text-lg",
          }}
        />
        <CardContent
          className={cn("flex flex-shrink-0 flex-grow flex-col gap-2 p-4", {
            "pb-0": isStepeprVisible,
          })}
        >
          <Typography variant={"body1"} className={"font-semibold"}>
            {contentTitle}
          </Typography>
          <Typography variant={"body1"}>
            <span
              dangerouslySetInnerHTML={{
                __html: sanitizedDescription,
              }}
            />
          </Typography>
          {hintCardDetailsButtonText && hintCardDetailsButton && (
            <Button
              variant={"text"}
              className={
                "m-0 min-h-0 w-fit min-w-0 justify-start p-0 text-base font-semibold text-blue hover:bg-transparent"
              }
              onClick={() => {
                trackAnalyticEvent("helper_clicked");
                openHintDetailsModal({
                  description: hintDetailsDescription || "",
                  actions: hintDetailsActions,
                  title: hintDetailsTitle || "",
                });
              }}
            >
              {hintCardDetailsButtonText}
            </Button>
          )}
        </CardContent>
        {isStepeprVisible && (
          <MobileStepper
            variant="text"
            position="static"
            steps={maxSteps}
            className="select-none font-sans text-sm text-black"
            activeStep={activeStep}
            nextButton={
              <IconButton
                size="small"
                onClick={onNextStep}
                className={cn("pointer-events-none invisible p-2", {
                  "pointer-events-auto visible": activeStep !== maxSteps - 1,
                })}
              >
                <WebShopIcon name="arrowRight" className="fill-blue" />
              </IconButton>
            }
            backButton={
              <IconButton
                size="small"
                onClick={onPrevStep}
                className={cn("pointer-events-none invisible p-2", {
                  "pointer-events-auto visible": activeStep > 0,
                })}
              >
                <WebShopIcon name="arrowLeft" className="fill-blue" />
              </IconButton>
            }
          />
        )}
      </Card>
      {/* <HintCardDetails
        open={isModalOpened}
        handleClose={handleModalClose}
        description={hintDetailsDescription}
        title={hintDetailsTitle}
        actions={hintDetailsActions}
      /> */}
    </>
  );
};
